import React from 'react';
import Link from 'next/link';
import {MUIAvatar, MUIBox, MUITypography} from '@/components/MUI-Components';
import {LinkedInBadgeIcon} from '@/components/shared/icons/SocialIcons';
import {decodeHtmlEntities} from '@/util/html-parser-utils';
import {colors, fontFamily, fontSize, fontWeight, borderRadius} from '@/styles/tokens';

// Normalize a profile URL to an absolute https link. Returns null for empty/missing
// values so we never render a dead `#` link that next/link resolves against the current
// route (which sends the user back to the blog instead of to LinkedIn).
const toAbsoluteUrl = (url?: string): string | null => {
    const trimmed = url?.trim();
    if (!trimmed) return null;
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

// Per-element sx constants driven by tokens.ts; responsive via MUI breakpoints.
// The shared .bdn-person-name/.bdn-person-role classes stay in SCSS (also used by
// the author bar). The Connect link always carries the `--outline` modifier, so only
// the outline style is rendered. `!important` is kept where MUI Avatar/Typography or
// the .bdn-blog-content article styles (this card sits inside it) would otherwise win.
const sxCards = {
    margin: '32px 0 16px',
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.lg,
    overflow: 'hidden',
    background: colors.background.default
} as const;

const sxCard = {
    padding: '20px',
    borderBottom: `1px solid ${colors.neutral.borderLight}`,
    '&:last-child': {borderBottom: 'none'}
} as const;

const sxTop = {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    flexWrap: {xs: 'wrap', sm: 'nowrap'}
} as const;

const sxAvatar = {width: '52px !important', height: '52px !important', flexShrink: 0} as const;

const sxInfo = {flex: 1, minWidth: 0} as const;

const sxRole = {mt: '3px !important'} as const;

const sxConnect = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: 'transparent',
    border: `1px solid ${colors.primary.main}`,
    color: `${colors.primary.main} !important`,
    fontFamily: fontFamily.body,
    fontSize: {xs: fontSize[12], sm: fontSize[14]},
    fontWeight: fontWeight.semibold,
    padding: {xs: '6px 12px', sm: '7px 16px'},
    borderRadius: borderRadius.full,
    textDecoration: 'none !important',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    transition: 'background 0.2s',
    '&:hover': {background: colors.primary.light}
} as const;

const sxDesc = {
    fontFamily: fontFamily.body,
    fontSize: `${fontSize[14]} !important`,
    color: `${colors.text.secondary} !important`,
    lineHeight: '1.65 !important',
    mt: '14px !important'
} as const;

const BlogAuthorCards = ({authorInfo, reviewerInfo}: {authorInfo: any; reviewerInfo?: any}) => {
    const authorLinkedin = toAbsoluteUrl(authorInfo.linkedin);
    const reviewerLinkedin = toAbsoluteUrl(reviewerInfo?.linkedin);

    return (
        <MUIBox sx={sxCards}>
            {/* Author card */}
            <MUIBox sx={sxCard}>
                <MUIBox sx={sxTop}>
                    <MUIAvatar src={authorInfo.image} alt={authorInfo.name || 'Author'} sx={sxAvatar} />
                    <MUIBox sx={sxInfo}>
                        <MUITypography className="bdn-person-name">{authorInfo.name || '—'}</MUITypography>
                        <MUITypography className="bdn-person-role" sx={sxRole}>
                            {authorInfo.designation}
                        </MUITypography>
                    </MUIBox>
                    {authorLinkedin && (
                        <MUIBox component={Link} href={authorLinkedin} target="_blank" rel="noopener noreferrer" sx={sxConnect}>
                            <LinkedInBadgeIcon />
                            Connect
                        </MUIBox>
                    )}
                </MUIBox>
                {authorInfo.bio && <MUITypography sx={sxDesc}>{decodeHtmlEntities(authorInfo.bio)}</MUITypography>}
            </MUIBox>

            {/* Reviewer card — only renders when backend sends `reviewer_info` */}
            {reviewerInfo && (
                <MUIBox sx={sxCard}>
                    <MUIBox sx={sxTop}>
                        <MUIAvatar src={reviewerInfo.image ?? ''} alt={reviewerInfo.name ?? 'Reviewer'} sx={sxAvatar} />
                        <MUIBox sx={sxInfo}>
                            <MUITypography className="bdn-person-name">{reviewerInfo.name ?? '—'}</MUITypography>
                            <MUITypography className="bdn-person-role" sx={sxRole}>
                                {reviewerInfo.designation ?? ''}
                            </MUITypography>
                        </MUIBox>
                        {reviewerLinkedin && (
                            <MUIBox component={Link} href={reviewerLinkedin} target="_blank" rel="noopener noreferrer" sx={sxConnect}>
                                <LinkedInBadgeIcon />
                                Connect
                            </MUIBox>
                        )}
                    </MUIBox>
                </MUIBox>
            )}
        </MUIBox>
    );
};

export default BlogAuthorCards;
